'use client';

import React, { useState } from 'react';

const stages = ['Pre-Seed', 'Seed', 'Series A', 'Series B+', 'Growth', 'Other'];

export default function FounderForm() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    website: '',
    linkedin: '',
    country: '',
    industry: '',
    stage: '',
    capitalRaising: '',
    capitalRaised: '',
    traction: '',
    useOfFunds: '',
    timeline: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bento-card rounded-3xl p-12 text-center">
        <div className="w-16 h-16 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-primary"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-3">Request Received</h3>
        <p className="text-sm text-muted-foreground font-light leading-relaxed max-w-md mx-auto">
          Thank you for submitting your fundraising request. We will review your opportunity and be
          in touch if there is a relevant match within our network.
        </p>
      </div>
    );
  }

  return (
    <div className="bento-card rounded-3xl p-8 sm:p-10">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-foreground mb-2">Raising Capital?</h2>
        <p className="text-sm text-muted-foreground font-light">
          Tell us about your company and fundraising requirements.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="your@email.com"
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Company *
            </label>
            <input
              type="text"
              name="company"
              required
              value={formData.company}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Company name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="https://yourcompany.com"
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              LinkedIn
            </label>
            <input
              type="url"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="LinkedIn profile URL"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Country of incorporation"
            />
          </div>
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Industry
            </label>
            <input
              type="text"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="e.g. FinTech, SaaS, AI"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Startup Stage *
            </label>
            <select
              name="stage"
              required
              value={formData.stage}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
            >
              <option value="">Select stage</option>
              {stages.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 5 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Capital Being Raised
            </label>
            <input
              type="text"
              name="capitalRaising"
              value={formData.capitalRaising}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="e.g. $2M"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Capital Already Raised
            </label>
            <input
              type="text"
              name="capitalRaised"
              value={formData.capitalRaised}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="e.g. $500K"
            />
          </div>
        </div>

        {/* Traction */}
        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Current Revenue / Traction
          </label>
          <input
            type="text"
            name="traction"
            value={formData.traction}
            onChange={handleChange}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            placeholder="e.g. $50K MRR, 200 customers"
          />
        </div>

        {/* Use of Funds */}
        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Use of Funds
          </label>
          <input
            type="text"
            name="useOfFunds"
            value={formData.useOfFunds}
            onChange={handleChange}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            placeholder="How will the capital be deployed?"
          />
        </div>

        {/* Timeline */}
        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Fundraising Timeline
          </label>
          <input
            type="text"
            name="timeline"
            value={formData.timeline}
            onChange={handleChange}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            placeholder="e.g. Looking to close within 3 months"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Message
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="Tell us about your company and what you're building..."
          />
        </div>

        <button
          type="submit"
          className="w-full py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300 hover:scale-[1.02] active:scale-100"
        >
          Submit Fundraising Request
        </button>

        <p className="text-xs text-muted-foreground font-light leading-relaxed text-center">
          Submission does not guarantee an investor introduction or investment. Opportunities are
          reviewed based on investor fit, mandate and availability.
        </p>
      </form>
    </div>
  );
}
